<template>
	<div
		class="message-media-container"
		:class="{ grid: showGrid, files: files.length > 1 }"
		@dragstart.prevent
	>
		<MediaGrid
			v-if="showGrid"
			:message="props.message"
			:autoPlayGif="autoPlayGif"
			@itemClick="onMediaClick"
		/>
		<VisualMedia
			v-if="singleVisual"
			:media="singleVisual"
			:autoPlayGif="autoPlayGif"
			rounded
			@click="onMediaClick(singleVisual)"
		/>
		<MediaFile
			v-for="file in files"
			:key="file.uuid || file.id"
			:media="file"
			:noDownload="file.isPDF"
			:class="{ [file.extension]: true }"
			@click="onMediaClick(file)"
		/>
		<AudioPlayer
			v-for="audio in audioFiles"
			:key="audio.uuid || audio.id"
			:src="audio.mediaUrl"
			:duration="audio.duration"
		/>
	</div>
</template>

<script setup>
import { computed } from 'vue'
import MediaGrid from '@/components/MediaView/MediaGrid'
import MediaFile from '@/components/MediaView/MediaFile'
import AudioPlayer from '@/components/MediaView/AudioPlayer'
import VisualMedia from '@/components/common/VisualMedia'
import { IMAGE_MAX_WIDTH } from '@/helpers/constants'
import { useMediaModal } from '@/components/common'
import { getStat } from '@/store'

const props = defineProps({ message: Object })
const visualMedia = computed(
	() => props.message?.messagemedia?.filter((m) => m.isVisual) || []
)
const showGrid = computed(() => visualMedia.value.length > 1)
const singleVisual = computed(() =>
	visualMedia.value.length === 1 ? visualMedia.value[0] : null
)
const files = computed(
	() => props.message?.messagemedia?.filter((m) => m.isFile) || []
)
const audioFiles = computed(
	() => props.message?.messagemedia?.filter((m) => m.isAudio) || []
)

const autoPlayGif = computed(() => {
	// should auto play if message is new and has no id
	if (!props.message.id) return true
	// otherwise, should only auto play if mark is unread
	const stats = getStat(props.message.bubble_id)
	return stats?.mark < props.message.id
})

const modal = useMediaModal()

const onMediaClick = (media) => {
	if (!props.message.isSent) return
	if (media.isVideo && singleVisual.value) return
	if (media.isFile && !media.isPDF) return
	// Pass the message object (not its id) so the lightbox works for messages
	// that aren't in the store — e.g. the activity feed normalizes its
	// messages with { skipStore: true }.
	modal.message(props.message, media.id)
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.message-media-container {
	cursor: pointer;
	@include flex-column(8px);
	--file-width: calc(v-bind(IMAGE_MAX_WIDTH) * 1px);
	&.grid,
	&.files {
		.media-file-container {
			width: var(--file-width);
		}
	}
	.media-file-container {
		max-width: var(--file-width);
		background: var(--neutral-0);
	}
}
</style>
